import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FolderOpen, Search, Filter, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, AlertCircle, Eye,
  Building, Calendar, Package, DollarSign, User,
  Globe, Clock, Shield, Info, MoreVertical,
  Ban, Pause, Play, Trash2, HardDrive, Plus, Minus, UserCheck,
  Users, UserPlus, UserMinus, Edit, ToggleLeft, ToggleRight, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import dataroomService from '../services/dataroomService';
import userService from '../services/userService';
import pricingPlanService from '../services/pricingPlanService';
import memberService from '../services/memberService';
import roleService from '../services/roleService';
import { useDebounce } from '../hooks/useDebounce';
import { TableLoader, FormLoader } from '../components/CommonLoader';
import FilterBar from '../components/FilterBar';
import StandardDataTable from '../components/StandardDataTable';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import { CreateButton, RefreshButton, DeleteButton, SaveButton } from '../components/CommonButtons';
import Switch from '../components/Switch';

const DataroomManagement = () => {
  const navigate = useNavigate();
  const [datarooms, setDatarooms] = useState([]);
  const [pendingDatarooms, setPendingDatarooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalDatarooms, setTotalDatarooms] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedDataroom, setSelectedDataroom] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [adminFilter, setAdminFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    pending: 0,
    suspended: 0
  });
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageDetails, setStorageDetails] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageAction, setStorageAction] = useState(''); // 'add' or 'remove'
  const [storageAmount, setStorageAmount] = useState('');
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  
  // Member management states
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [dataroomMembers, setDataroomMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState('1'); // 1 for active, 0 for inactive
  const [selectedMemberDataroom, setSelectedMemberDataroom] = useState(null);
  const [dataroomRoles, setDataroomRoles] = useState([]);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMemberData, setEditMemberData] = useState({
    dataroom_role_id: '',
    custom_permissions: []
  });

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowActionsMenu({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchRegions();
    fetchUsers();
    fetchPlans();
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAllDatarooms();
    } else {
      fetchPendingDatarooms();
    }
  }, [currentPage, debouncedSearchTerm, selectedStatus, selectedApprovalStatus, perPage, activeTab, adminFilter, planFilter]);

  const fetchRegions = async () => {
    try {
      const response = await dataroomService.getAWSRegions();
      // Use normalized response structure
      setRegions(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch AWS regions');
    }
  };

  // Close region dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRegionDropdown && !event.target.closest('.region-dropdown-container')) {
        setShowRegionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRegionDropdown]);

  const fetchUsers = async () => {
    try {
      const response = await userService.getUsers({ per_page: 100 });
      // Use normalized response structure - users will have integer IDs
      setUsers(response.data || []);
    } catch (error) {
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await pricingPlanService.getPricingPlans();
      // Use normalized response structure - plans will have integer IDs
      setPlans(response.data || []);
    } catch (error) {
    }
  };

  const fetchAllDatarooms = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        status: selectedStatus,
        approval_status: selectedApprovalStatus,
        admin_id: adminFilter,
        pricing_plan_id: planFilter,
        sort_by: 'created_at',
        sort_order: 'desc'
      };

      const response = await dataroomService.getAllDatarooms(params);
      
      // Use normalized response structure
      const dataroomsList = response.data || [];
      
      setDatarooms(dataroomsList);
      if (response.pagination) {
        setTotalDatarooms(response.pagination.total || 0);
        setTotalPages(response.pagination.last_page || 1);
        setCurrentPage(response.pagination.current_page || 1);
      }
      
      // Calculate statistics
      const stats = dataroomsList.reduce((acc, dataroom) => {
        acc.total++;
        // Check for active status (1 or 'active')
        if (dataroom.status === 1 || dataroom.status === '1' || dataroom.status === 'active') {
          acc.active++;
        }
        // Check for pending approval (0 or 'pending')
        if (dataroom.approval_status === 0 || dataroom.approval_status === '0' || dataroom.approval_status === 'pending') {
          acc.pending++;
        }
        // Check for suspended status (2 or 'suspended')
        if (dataroom.status === 2 || dataroom.status === '2' || dataroom.status === 'suspended') {
          acc.suspended++;
        }
        return acc;
      }, { total: 0, active: 0, pending: 0, suspended: 0 });
      
      setStatistics(stats);
    } catch (error) {
      toast.error('Failed to fetch datarooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingDatarooms = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        admin_id: adminFilter,
        pricing_plan_id: planFilter
      };

      const response = await dataroomService.getPendingDatarooms(params);
      
      // Use normalized response structure
      const dataroomsList = response.data || [];
      
      setPendingDatarooms(dataroomsList);
      if (response.pagination) {
        setTotalDatarooms(response.pagination.total || 0);
        setTotalPages(response.pagination.last_page || 1);
        setCurrentPage(response.pagination.current_page || 1);
      }
    } catch (error) {
      toast.error('Failed to fetch pending datarooms');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowApprovalModal(true);
  };

  const confirmApproval = async () => {
    if (!selectedRegion) {
      toast.error('Please select a region');
      return;
    }

    setIsApproving(true);
    const wasInactive = selectedDataroom.status === 0 || selectedDataroom.status === 'inactive' || 
                        selectedDataroom.status === 'suspended' || selectedDataroom.status === 2;
    const actionText = wasInactive ? 'Activating' : 'Approving';
    const toastId = toast.loading(`${actionText} dataroom...`);

    try {
      await dataroomService.approveDataroom(selectedDataroom.uuid, selectedRegion);
      
      // Check if this was activation or approval
      const wasInactive = selectedDataroom.status === 0 || selectedDataroom.status === 'inactive' || 
                          selectedDataroom.status === 'suspended' || selectedDataroom.status === 2;
      
      if (wasInactive) {
        toast.success('Dataroom activated successfully', { id: toastId });
      } else {
        toast.success('Dataroom approved successfully', { id: toastId });
      }
      
      setShowApprovalModal(false);
      setSelectedDataroom(null);
      setSelectedRegion('');
      
      if (activeTab === 'pending') {
        fetchPendingDatarooms();
      } else {
        fetchAllDatarooms();
      }
    } catch (error) {
      
      // Check if error is due to already approved/active
      if (error.response?.data?.message?.includes('already') || error.response?.data?.message?.includes('approved')) {
        toast.info('Dataroom is already active/approved');
        setShowApprovalModal(false);
        setSelectedDataroom(null);
        setSelectedRegion('');
        fetchAllDatarooms();
      } else {
        toast.error(error.response?.data?.message || 'Failed to process dataroom', { id: toastId });
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleViewDetails = async (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowDetailsModal(true);
    
    try {
      const response = await dataroomService.getDataroomDetailsAdmin(dataroom.uuid);
      setSelectedDataroom(response.data || response);
    } catch (error) {
      toast.error('Failed to fetch dataroom details');
    }
  };

  const handleReject = (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await dataroomService.rejectDataroom(selectedDataroom.uuid, rejectReason);
      toast.success('Dataroom rejected successfully');
      setShowRejectModal(false);
      setSelectedDataroom(null);
      setRejectReason('');
      
      if (activeTab === 'pending') {
        fetchPendingDatarooms();
      } else {
        fetchAllDatarooms();
      }
    } catch (error) {
      toast.error('Failed to reject dataroom');
    }
  };

  const handleSuspend = (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowSuspendModal(true);
  };

  const confirmSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }

    try {
      await dataroomService.suspendDataroom(selectedDataroom.uuid, suspendReason);
      toast.success('Dataroom suspended successfully');
      setShowSuspendModal(false);
      setSelectedDataroom(null);
      setSuspendReason('');
      setShowActionsMenu({});
      fetchAllDatarooms();
    } catch (error) {
      toast.error('Failed to suspend dataroom');
    }
  };

  const handleToggleStatus = async (dataroomUuid) => {
    try {
      await dataroomService.toggleDataroomStatus(dataroomUuid);
      toast.success('Dataroom status toggled successfully');

      if (activeTab === 'pending') {
        fetchPendingDatarooms();
      } else {
        fetchAllDatarooms();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle dataroom status');
    }
  };

  const handleDelete = async (dataroom) => {
    if (window.confirm(`Are you sure you want to delete the dataroom "${dataroom.name}"? This action cannot be undone.`)) {
      try {
        await dataroomService.deleteDataroomAdmin(dataroom.uuid);
        toast.success('Dataroom deleted successfully');
        setShowActionsMenu({});
        fetchAllDatarooms();
      } catch (error) {
        toast.error('Failed to delete dataroom');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleManageRoles = (dataroom) => {
    // Navigate to detail page with roles tab
    navigate(`/dataroom-management/${dataroom.uuid}?tab=roles`);
  };

  // Commented out - Manage Members functionality (members can be managed in DataroomDetail)
  // const handleManageMembers = async (dataroom) => {
  //   setSelectedMemberDataroom(dataroom);
  //   setShowMembersModal(true);
  //   setShowActionsMenu({});
  //   await fetchDataroomMembers(dataroom.uuid);
  //   await fetchDataroomRoles(dataroom.uuid);
  // };

  const fetchDataroomMembers = async (dataroomUuid) => {
    try {
      setMembersLoading(true);
      const params = {
        is_active: memberStatusFilter,
        role_id: memberRoleFilter,
        search: memberSearchTerm
      };
      const response = await memberService.superAdminDataroom.getUsers(dataroomUuid, params);
      setDataroomMembers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch dataroom members');
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchDataroomRoles = async (dataroomUuid) => {
    try {
      const response = await roleService.getRoles(dataroomUuid);
      setDataroomRoles(response.data || []);
    } catch (error) {
    }
  };

  const handleToggleMemberStatus = async (member) => {
    try {
      await memberService.superAdminDataroom.toggleUserStatus(selectedMemberDataroom.uuid, member.id);
      toast.success(`User ${member.is_active ? 'deactivated' : 'activated'} successfully`);
      await fetchDataroomMembers(selectedMemberDataroom.uuid);
    } catch (error) {
      toast.error('Failed to toggle member status');
    }
  };

  const handleEditMember = async (member) => {
    setSelectedMember(member);
    setEditMemberData({
      dataroom_role_id: member.dataroom_role_id || '',
      custom_permissions: member.custom_permissions || []
    });
    setShowEditMemberModal(true);
  };

  const handleUpdateMember = async () => {
    try {
      await memberService.superAdminDataroom.updateUser(
        selectedMemberDataroom.uuid,
        selectedMember.id,
        editMemberData
      );
      toast.success('Member updated successfully');
      setShowEditMemberModal(false);
      await fetchDataroomMembers(selectedMemberDataroom.uuid);
    } catch (error) {
      toast.error('Failed to update member');
    }
  };

  const handleDeleteMember = async (member) => {
    if (window.confirm(`Are you sure you want to remove ${member.first_name} ${member.last_name} from this dataroom?`)) {
      try {
        await memberService.superAdminDataroom.deleteUser(selectedMemberDataroom.uuid, member.id);
        toast.success('Member removed successfully');
        await fetchDataroomMembers(selectedMemberDataroom.uuid);
      } catch (error) {
        toast.error('Failed to remove member');
      }
    }
  };

  const handleNavigateToDetail = (dataroom) => {
    // Navigate to detail page
    navigate(`/dataroom-management/${dataroom.uuid}`);
  };

  const handleManageStorage = async (dataroom) => {
    if (!dataroom.admin?.id) {
      toast.error('Admin information not available');
      return;
    }

    setSelectedDataroom(dataroom);
    setShowStorageModal(true);
    setStorageLoading(true);

    try {
      const response = await dataroomService.getDataroomStorage(dataroom.admin.id);
      setStorageDetails(response.data || response);
    } catch (error) {
      toast.error('Failed to fetch storage details');
    } finally {
      setStorageLoading(false);
    }
  };

  const handleStorageUpdate = async () => {
    if (!storageAmount || isNaN(storageAmount) || parseInt(storageAmount) <= 0) {
      toast.error('Please enter a valid storage amount');
      return;
    }

    try {
      const adminId = selectedDataroom.admin.id;
      const data = { storage_amount: parseInt(storageAmount) };

      if (storageAction === 'add') {
        await dataroomService.addDataroomStorage(adminId, data);
        toast.success(`Successfully added ${storageAmount}GB storage`);
      } else {
        await dataroomService.removeDataroomStorage(adminId, data);
        toast.success(`Successfully removed ${storageAmount}GB storage`);
      }

      // Refresh storage details
      const response = await dataroomService.getDataroomStorage(adminId);
      setStorageDetails(response.data || response);
      setStorageAmount('');
      setStorageAction('');
    } catch (error) {
      toast.error(`Failed to ${storageAction} storage`);
    }
  };

  const getStatusBadge = (status) => {
    // Handle numeric status values: 0 = Not Active, 1 = Active
    let statusKey;
    let label;
    
    if (status === 1 || status === '1' || status === 'active') {
      statusKey = 'active';
      label = 'Active';
    } else if (status === 0 || status === '0' || status === 'not_active' || status === 'inactive') {
      statusKey = 'not_active';
      label = 'Not Active';
    } else if (status === 2 || status === '2' || status === 'suspended') {
      statusKey = 'suspended';
      label = 'Suspended';
    } else {
      statusKey = 'not_active';
      label = 'Not Active';
    }
    
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      not_active: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      suspended: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    
    const config = statusConfig[statusKey];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </span>
    );
  };

  const getApprovalBadge = (dataroom) => {
    // Handle null/undefined dataroom
    if (!dataroom) {
      return <span className="text-gray-400">N/A</span>;
    }
    
    // Handle numeric approval_status values: 0 = Pending, 1 = Approved, 2 = Rejected
    let approvalKey;
    let label;
    
    const approvalStatus = dataroom.approval_status;
    
    if (approvalStatus === 1 || approvalStatus === '1' || approvalStatus === 'approved') {
      approvalKey = 'approved';
      label = 'Approved';
    } else if (approvalStatus === 0 || approvalStatus === '0' || approvalStatus === 'pending') {
      approvalKey = 'pending';
      label = 'Pending';
    } else if (approvalStatus === 2 || approvalStatus === '2' || approvalStatus === 'rejected') {
      approvalKey = 'rejected';
      label = 'Rejected';
    } else {
      approvalKey = 'pending';
      label = 'Pending';
    }
    
    const statusConfig = {
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[approvalKey];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </span>
    );
  };

  const displayDatarooms = activeTab === 'all' ? datarooms : pendingDatarooms;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Dataroom Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage and approve datarooms across the platform
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('all');
              setCurrentPage(1);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Datarooms
          </button>
          <button
            onClick={() => {
              setActiveTab('pending');
              setCurrentPage(1);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approval
          </button>
        </nav>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search datarooms..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            {activeTab === 'all' && (
              <>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                  <option value="2">Suspended</option>
                </select>

                <select
                  value={selectedApprovalStatus}
                  onChange={(e) => setSelectedApprovalStatus(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Approval Status</option>
                  <option value="1">Approved</option>
                  <option value="0">Pending</option>
                  <option value="2">Rejected</option>
                </select>

                <select
                  value={adminFilter}
                  onChange={(e) => setAdminFilter(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Users</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>

                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Plans</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
      </div>

      {/* Datarooms Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <EnhancedDataTable
          data={displayDatarooms}
          columns={[
            {
              key: 'name',
              header: 'Dataroom',
              sortable: true,
              render: (value, row) => (
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {(row.logo_url || row.logo) ? (
                      <img
                        className="h-10 w-10 rounded-full object-cover"
                        src={row.logo_url || row.logo}
                        alt={row.name}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <FolderOpen className="h-5 w-5 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div 
                      className="text-sm font-medium text-primary-600 hover:text-primary-800 cursor-pointer transition-colors"
                      onClick={() => handleNavigateToDetail(row)}
                      title="Click to view dataroom details"
                    >
                      {row.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {row.company_name}
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'admin',
              header: 'Admin',
              sortable: true,
              render: (value, row) => (
                <div>
                  <div className="text-sm text-gray-900">
                    {row.admin?.first_name} {row.admin?.last_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {row.admin?.email}
                  </div>
                </div>
              )
            },
            {
              key: 'pricing_plan',
              header: 'Plan',
              sortable: true,
              render: (value, row) => (
                <div>
                  <div className="text-sm text-gray-900">
                    {row.pricing_plan?.name || 'N/A'}
                  </div>
                  {row.pricing_plan && (
                    <div className="text-sm text-gray-500">
                      ₹{row.pricing_plan.price}/mo
                    </div>
                  )}
                </div>
              )
            },
            {
              key: 'status',
              header: 'Status',
              sortable: true,
              render: (value, row) => (
                <Switch
                  checked={row.status === 1 || row.status === 'active'}
                  onChange={() => handleToggleStatus(row.uuid)}
                  tooltip={row.status === 1 || row.status === 'active' ? 'Click to deactivate' : 'Click to activate'}
                />
              )
            },
            ...(activeTab === 'all' ? [{
              key: 'approval_status',
              header: 'Approval',
              sortable: true,
              render: (value, row) => getApprovalBadge(row)
            }] : []),
            {
              key: 'created_at',
              header: 'Created',
              sortable: true,
              render: (value) => (
                <span className="text-sm text-gray-500">
                  {formatDate(value)}
                </span>
              )
            }
          ]}
          actions={(row) => [
            {
              icon: Eye,
              label: 'View Details',
              onClick: () => handleNavigateToDetail(row)
            },
            ...((row.approval_status === 0 || row.approval_status === '0' || row.approval_status === 'pending') ? [
              {
                icon: CheckCircle,
                label: 'Approve Dataroom',
                onClick: () => handleApprove(row),
                className: 'text-green-600 hover:text-green-800'
              },
              {
                icon: XCircle,
                label: 'Reject Dataroom',
                onClick: () => handleReject(row),
                className: 'text-red-600 hover:text-red-800'
              }
            ] : []),
            ...((row.status === 1 || row.status === 'active') ? [
              {
                icon: Pause,
                label: 'Suspend',
                onClick: () => handleSuspend(row)
              }
            ] : [])
          ]}
          loading={loading}
          emptyMessage="No datarooms found"
          emptyIcon={FolderOpen}
          showColumnControls={true}
          enableColumnResize={true}
          enableDragDrop={true}
          onSort={(key, direction) => {
            const sorted = [...displayDatarooms].sort((a, b) => {
              let aVal = a[key];
              let bVal = b[key];
              
              if (key === 'admin') {
                aVal = `${a.admin?.first_name} ${a.admin?.last_name}`;
                bVal = `${b.admin?.first_name} ${b.admin?.last_name}`;
              } else if (key === 'pricing_plan') {
                aVal = a.pricing_plan?.name || '';
                bVal = b.pricing_plan?.name || '';
              } else if (key === 'created_at') {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
              }
              
              if (aVal < bVal) return direction === 'asc' ? -1 : 1;
              if (aVal > bVal) return direction === 'asc' ? 1 : -1;
              return 0;
            });
            setDatarooms(sorted);
          }}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, totalDatarooms)}
                  </span>{' '}
                  of <span className="font-medium">{totalDatarooms}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4">
              {(selectedDataroom.status === 0 || selectedDataroom.status === 'inactive' || 
                selectedDataroom.status === 'suspended' || selectedDataroom.status === 2) 
                ? 'Activate Dataroom' : 'Approve Dataroom'}
            </h3>
            <p className="text-gray-600 mb-4">
              You are about to {(selectedDataroom.status === 0 || selectedDataroom.status === 'inactive' || 
                selectedDataroom.status === 'suspended' || selectedDataroom.status === 2) 
                ? 'activate' : 'approve'} the dataroom "{selectedDataroom.name}". 
              Please select the AWS region for deployment.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AWS Region
              </label>
              <div className="relative region-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    value={regionSearch}
                    onChange={(e) => {
                      setRegionSearch(e.target.value);
                      setShowRegionDropdown(true);
                    }}
                    onFocus={() => setShowRegionDropdown(true)}
                    placeholder={selectedRegion 
                      ? regions.find(r => r.id === selectedRegion)?.name + ' (' + regions.find(r => r.id === selectedRegion)?.code + ')'
                      : "Search and select a region"
                    }
                    className="w-full px-3 py-1.5 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegionDropdown(!showRegionDropdown)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </div>
                
                {showRegionDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {regions
                      .filter(region => {
                        const searchLower = regionSearch.toLowerCase();
                        return region.name.toLowerCase().includes(searchLower) || 
                               region.code.toLowerCase().includes(searchLower);
                      })
                      .map((region) => (
                        <button
                          key={region.id}
                          type="button"
                          onClick={() => {
                            setSelectedRegion(region.id);
                            setRegionSearch('');
                            setShowRegionDropdown(false);
                          }}
                          className={`w-full px-3 py-1.5 text-left hover:bg-gray-100 flex items-center justify-between ${
                            selectedRegion === region.id ? 'bg-blue-50 text-blue-600' : ''
                          }`}
                        >
                          <span>{region.name}</span>
                          <span className="text-sm text-gray-500">({region.code})</span>
                        </button>
                      ))}
                    {regions.filter(region => {
                      const searchLower = regionSearch.toLowerCase();
                      return region.name.toLowerCase().includes(searchLower) || 
                             region.code.toLowerCase().includes(searchLower);
                    }).length === 0 && (
                      <div className="px-3 py-1.5 text-gray-500 text-center">
                        No regions found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedDataroom(null);
                  setSelectedRegion('');
                  setRegionSearch('');
                  setShowRegionDropdown(false);
                  setIsApproving(false);
                }}
                disabled={isApproving}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproval}
                disabled={!selectedRegion || isApproving}
                className="bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[150px] px-3 py-1.5 text-sm"
              >
                {isApproving && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isApproving ? 'Processing...' : 
                  (selectedDataroom.status === 0 || selectedDataroom.status === 'inactive' || 
                    selectedDataroom.status === 'suspended' || selectedDataroom.status === 2) 
                    ? 'Activate Dataroom' : 'Approve Dataroom'
                }
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Dataroom Details</h3>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedDataroom(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Logo and Basic Info */}
              <div className="flex items-start mb-6">
                {selectedDataroom.logo ? (
                  <img
                    className="h-20 w-20 rounded-lg object-cover"
                    src={selectedDataroom.logo}
                    alt={selectedDataroom.name}
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-gray-300 flex items-center justify-center">
                    <FolderOpen className="h-10 w-10 text-gray-600" />
                  </div>
                )}
                <div className="ml-4">
                  <h4 className="text-xl font-semibold">{selectedDataroom.name}</h4>
                  <p className="text-gray-600">{selectedDataroom.company_name}</p>
                  <div className="mt-2 flex space-x-2">
                    {getStatusBadge(selectedDataroom.status)}
                    {getApprovalBadge(selectedDataroom)}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Building className="w-5 h-5 mr-2" />
                    Organization Details
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">Brand Name</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.brand_name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Organization Type</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.organization_type || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Industry</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.industry || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Transaction Type</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.transaction_type || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    Compliance Details
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">CIN</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.corporate_identification_no || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">PAN</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.pan_number || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">TAN</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.tan_number || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">GSTIN</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.gstin || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Contact Information
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">Organization Email</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.organization_email || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Alternate Contact</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.alternate_contact_person_name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Contact Number</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.alternate_contact_person_no || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Registered Address</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.registered_office_address || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Subscription Details
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">Pricing Plan</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.pricing_plan?.name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Price</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan ? `₹${selectedDataroom.pricing_plan.price}/mo` : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Storage</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan ? `${selectedDataroom.pricing_plan.storage} GB` : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">User Limit</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan?.user_limit || 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Admin Information */}
              {selectedDataroom.admin && (
                <div className="mt-6 pt-6 border-t">
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Admin Information
                  </h5>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-gray-500">Name</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.admin.first_name} {selectedDataroom.admin.last_name}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Email</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.admin.email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Phone</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.admin.phone_no || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Role</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.admin.role || 'Admin'}</dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Timestamps */}
              <div className="mt-6 pt-6 border-t">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Created At</dt>
                    <dd className="text-sm font-medium">{formatDate(selectedDataroom.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Updated At</dt>
                    <dd className="text-sm font-medium">{formatDate(selectedDataroom.updated_at)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4">Reject Dataroom</h3>
            <p className="text-gray-600 mb-4">
              You are about to reject the dataroom "{selectedDataroom.name}". Please provide a reason for rejection.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                placeholder="Enter the reason for rejection..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedDataroom(null);
                  setRejectReason('');
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim()}
                className="bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-sm"
              >
                Reject Dataroom
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 text-orange-500 mr-2" />
              Suspend Dataroom
            </h3>
            <p className="text-gray-600 mb-4">
              You are about to suspend the dataroom "{selectedDataroom.name}". This will temporarily disable access to the dataroom.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suspension Reason
              </label>
              <textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                placeholder="Enter the reason for suspension..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSelectedDataroom(null);
                  setSuspendReason('');
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSuspend}
                disabled={!suspendReason.trim()}
                className="bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-sm"
              >
                Suspend Dataroom
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Storage Management Modal */}
      {showStorageModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <HardDrive className="w-5 h-5 text-blue-500 mr-2" />
              Manage Storage - {selectedDataroom.name}
            </h3>
            
            {storageLoading ? (
              <FormLoader />
            ) : (
              <>
                {/* Admin Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Admin Name</p>
                      <p className="font-medium">{selectedDataroom.admin?.first_name} {selectedDataroom.admin?.last_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Admin Email</p>
                      <p className="font-medium">{selectedDataroom.admin?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Current Storage Details */}
                {storageDetails && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Current Storage Allocation</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Total Storage</p>
                        <p className="text-2xl font-semibold text-blue-600">
                          {storageDetails.total_storage || 0} GB
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Used Storage</p>
                        <p className="text-2xl font-semibold text-green-600">
                          {storageDetails.used_storage || 0} GB
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Available Storage</p>
                        <p className="text-2xl font-semibold text-purple-600">
                          {(storageDetails.total_storage || 0) - (storageDetails.used_storage || 0)} GB
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Storage Action Selection */}
                {!storageAction && (
                  <div className="flex justify-center space-x-4 mb-6">
                    <button
                      onClick={() => setStorageAction('add')}
                      className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Storage
                    </button>
                    <button
                      onClick={() => setStorageAction('remove')}
                      disabled={!storageDetails || storageDetails.total_storage === 0}
                      className="flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-5 h-5 mr-2" />
                      Remove Storage
                    </button>
                  </div>
                )}

                {/* Storage Amount Input */}
                {storageAction && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Storage Amount (GB)
                    </label>
                    <input
                      type="number"
                      value={storageAmount}
                      onChange={(e) => setStorageAmount(e.target.value)}
                      min="1"
                      className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                      placeholder={`Enter amount to ${storageAction}...`}
                    />
                    {storageAction === 'remove' && storageDetails && (
                      <p className="text-sm text-gray-500 mt-2">
                        Maximum removable: {storageDetails.total_storage - storageDetails.used_storage} GB
                      </p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  {storageAction && (
                    <button
                      onClick={() => {
                        setStorageAction('');
                        setStorageAmount('');
                      }}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowStorageModal(false);
                      setSelectedDataroom(null);
                      setStorageDetails(null);
                      setStorageAction('');
                      setStorageAmount('');
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  {storageAction && (
                    <button
                      onClick={handleStorageUpdate}
                      disabled={!storageAmount || parseInt(storageAmount) <= 0}
                      className={`px-3 py-1.5 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                        storageAction === 'add' 
                          ? 'bg-green-600 hover:bg-green-700' 
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {storageAction === 'add' ? 'Add Storage' : 'Remove Storage'}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Members Management Modal */}
      {showMembersModal && selectedMemberDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">
                  Manage Members - {selectedMemberDataroom.name}
                </h3>
                <button
                  onClick={() => {
                    setShowMembersModal(false);
                    setSelectedMemberDataroom(null);
                    setDataroomMembers([]);
                    setMemberSearchTerm('');
                    setMemberRoleFilter('');
                    setMemberStatusFilter('1');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {/* Filters */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative max-w-md">
                  <input
                    type="text"
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    placeholder="Search members..."
                    className="w-full pl-10 pr-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                
                <select
                  value={memberStatusFilter}
                  onChange={(e) => setMemberStatusFilter(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Active Members</option>
                  <option value="0">Inactive Members</option>
                  <option value="">All Members</option>
                </select>

                <select
                  value={memberRoleFilter}
                  onChange={(e) => setMemberRoleFilter(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Roles</option>
                  {dataroomRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>

              </div>

              {/* Members Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="relative px-3 py-1.5">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {membersLoading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4">
                          <TableLoader rows={5} columns={6} />
                        </td>
                      </tr>
                    ) : dataroomMembers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                          No members found
                        </td>
                      </tr>
                    ) : (
                      dataroomMembers.map((member) => (
                        <tr key={member.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-gray-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {member.first_name} {member.last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {member.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{member.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {member.dataroom_role?.name || 'No Role'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              member.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {member.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(member.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditMember(member)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit Member"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleMemberStatus(member)}
                                className={`${
                                  member.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                                }`}
                                title={member.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {member.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleDeleteMember(member)}
                                className="text-red-600 hover:text-red-900"
                                title="Remove Member"
                              >
                                <UserMinus className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditMemberModal && selectedMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4">
              Edit Member - {selectedMember.first_name} {selectedMember.last_name}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dataroom Role
              </label>
              <select
                value={editMemberData.dataroom_role_id}
                onChange={(e) => setEditMemberData({...editMemberData, dataroom_role_id: e.target.value})}
                className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
              >
                <option value="">Select Role</option>
                {dataroomRoles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditMemberModal(false);
                  setSelectedMember(null);
                  setEditMemberData({ dataroom_role_id: '', custom_permissions: [] });
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMember}
                className="bg-blue-600 text-white rounded-md hover:bg-blue-700 px-3 py-1.5 text-sm"
              >
                Update Member
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DataroomManagement;